import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import tutorialProgressService from '@/services/tutorialProgressService';

interface TutorialState {
  tutorialId: string | null;
  currentStep: number;
  totalSteps: number;
  completedSteps: number[];
  isPlaying: boolean;
  progress: number;
}

const initialState: TutorialState = {
  tutorialId: null,
  currentStep: 1,
  totalSteps: 6,
  completedSteps: [],
  isPlaying: false,
  progress: 0,
};

const TUTORIAL_ACTIONS = {
  SET_CURRENT_STEP: 'SET_CURRENT_STEP',
  NEXT_STEP: 'NEXT_STEP',
  PREV_STEP: 'PREV_STEP',
  COMPLETE_STEP: 'COMPLETE_STEP',
  SET_PLAYING: 'SET_PLAYING',
  LOAD_PROGRESS: 'LOAD_PROGRESS',
  RESET_PROGRESS: 'RESET_PROGRESS',
} as const;

type TutorialAction =
  | { type: 'SET_CURRENT_STEP'; payload: number }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'COMPLETE_STEP'; payload: number }
  | { type: 'SET_PLAYING'; payload: boolean }
  | { type: 'LOAD_PROGRESS'; payload: Partial<TutorialState> }
  | { type: 'RESET_PROGRESS' };

function tutorialReducer(state: TutorialState, action: TutorialAction): TutorialState {
  switch (action.type) {
    case 'SET_CURRENT_STEP': {
      const newStep = Math.max(1, Math.min(action.payload, state.totalSteps));
      const newProgress = state.totalSteps > 1 ? ((newStep - 1) / (state.totalSteps - 1)) * 100 : 0;
      return { ...state, currentStep: newStep, progress: newProgress };
    }
    case 'NEXT_STEP':
      if (state.currentStep < state.totalSteps) {
        const nextStep = state.currentStep + 1;
        const progress = ((nextStep - 1) / (state.totalSteps - 1)) * 100;
        return { ...state, currentStep: nextStep, progress };
      }
      return state;
    case 'PREV_STEP':
      if (state.currentStep > 1) {
        const prevStep = state.currentStep - 1;
        const progress = ((prevStep - 1) / (state.totalSteps - 1)) * 100;
        return { ...state, currentStep: prevStep, progress };
      }
      return state;
    case 'COMPLETE_STEP': {
      const stepToComplete = action.payload;
      if (!state.completedSteps.includes(stepToComplete)) {
        return { ...state, completedSteps: [...state.completedSteps, stepToComplete] };
      }
      return state;
    }
    case 'SET_PLAYING':
      return { ...state, isPlaying: action.payload };
    case 'LOAD_PROGRESS':
      return { ...state, ...action.payload };
    case 'RESET_PROGRESS':
      return initialState;
    default:
      return state;
  }
}

interface TutorialContextValue extends TutorialState {
  goToStep: (stepNumber: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  completeStep: (stepNumber: number) => void;
  setPlaying: (isPlaying: boolean) => void;
  resetProgress: () => Promise<void>;
  isStepCompleted: (stepNumber: number) => boolean;
  isCurrentStep: (stepNumber: number) => boolean;
  initializeTutorial: (tutorialId: string, totalSteps: number) => void;
}

const TutorialContext = createContext<TutorialContextValue | null>(null);

interface TutorialProviderProps {
  children: React.ReactNode;
  tutorialId?: string | null;
}

export function TutorialProvider({ children, tutorialId = null }: TutorialProviderProps) {
  const [state, dispatch] = useReducer(tutorialReducer, { ...initialState, tutorialId });

  useEffect(() => {
    const loadProgress = async () => {
      if (!tutorialId) {
        const savedProgress = localStorage.getItem('tutorialProgress');
        if (savedProgress) {
          try {
            const progressData = JSON.parse(savedProgress);
            dispatch({ type: 'LOAD_PROGRESS', payload: progressData });
          } catch (error) {
            console.error('Erro ao carregar progresso:', error);
          }
        }
        return;
      }

      try {
        const apiProgress = await tutorialProgressService.getProgress(tutorialId);
        if (apiProgress?.data) {
          dispatch({ type: 'LOAD_PROGRESS', payload: apiProgress.data });
        } else {
          const localProgress = tutorialProgressService.getProgressLocal(tutorialId);
          if (localProgress) {
            dispatch({ type: 'LOAD_PROGRESS', payload: localProgress });
            tutorialProgressService.syncProgress(tutorialId);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar progresso:', error);
        const localProgress = tutorialProgressService.getProgressLocal(tutorialId);
        if (localProgress) {
          dispatch({ type: 'LOAD_PROGRESS', payload: localProgress });
        }
      }
    };

    loadProgress();
  }, [tutorialId]);

  useEffect(() => {
    if (!tutorialId) {
      const progressData = {
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        progress: state.progress,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem('tutorialProgress', JSON.stringify(progressData));
      return;
    }

    const saveProgress = async () => {
      const progressData = {
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        progress: state.progress,
        totalSteps: state.totalSteps,
        timestamp: new Date().toISOString(),
      };

      try {
        const result = await tutorialProgressService.saveProgress(tutorialId, progressData);
        if (!result) {
          tutorialProgressService.saveProgressLocal(tutorialId, progressData);
        }
      } catch {
        tutorialProgressService.saveProgressLocal(tutorialId, progressData);
      }
    };

    const timeoutId = setTimeout(saveProgress, 1000);
    return () => clearTimeout(timeoutId);
  }, [state.currentStep, state.completedSteps, state.progress, state.totalSteps, tutorialId]);

  const goToStep = useCallback((stepNumber: number) => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: stepNumber });
  }, []);

  const nextStep = useCallback(() => {
    dispatch({ type: 'NEXT_STEP' });
  }, []);

  const prevStep = useCallback(() => {
    dispatch({ type: 'PREV_STEP' });
  }, []);

  const completeStep = useCallback((stepNumber: number) => {
    dispatch({ type: 'COMPLETE_STEP', payload: stepNumber });
  }, []);

  const setPlaying = useCallback((isPlaying: boolean) => {
    dispatch({ type: 'SET_PLAYING', payload: isPlaying });
  }, []);

  const resetProgress = useCallback(async () => {
    dispatch({ type: 'RESET_PROGRESS' });

    if (tutorialId) {
      try {
        await tutorialProgressService.saveProgress(tutorialId, {
          currentStep: 1,
          completedSteps: [],
          progress: 0,
          totalSteps: state.totalSteps,
        });
      } catch {
        // ignore
      }
      localStorage.removeItem(`tutorial_progress_${tutorialId}`);
    } else {
      localStorage.removeItem('tutorialProgress');
    }
  }, [tutorialId, state.totalSteps]);

  const isStepCompleted = useCallback((stepNumber: number) => state.completedSteps.includes(stepNumber), [state.completedSteps]);
  const isCurrentStep = useCallback((stepNumber: number) => state.currentStep === stepNumber, [state.currentStep]);

  const initializeTutorial = useCallback((tutorialIdParam: string, totalSteps: number) => {
    dispatch({
      type: 'LOAD_PROGRESS',
      payload: {
        tutorialId: tutorialIdParam,
        totalSteps,
        currentStep: 1,
        completedSteps: [],
        progress: 0,
      },
    });
  }, []);

  const value: TutorialContextValue = {
    ...state,
    goToStep,
    nextStep,
    prevStep,
    completeStep,
    setPlaying,
    resetProgress,
    isStepCompleted,
    isCurrentStep,
    initializeTutorial,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial(): TutorialContextValue {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial deve ser usado dentro de um TutorialProvider');
  }
  return context;
}
